import React, { useState, useRef } from "react";
import {
    Paperclip,
    Camera,
    Video,
    Square,
    X,
    FileIcon,
    Film,
    Image,
    Download,
} from "lucide-react";
import { toast } from "sonner";

function PendingPreviewModal({ file, onClose }) {
    if (!file) return null;
    const isImage = file.file.type.startsWith("image/");
    const isVideo = file.file.type.startsWith("video/");
    const isPdf = file.file.type === "application/pdf";
    const url = file.preview || URL.createObjectURL(file.file);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            data-testid="pending-preview-overlay"
        >
            <div
                className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-[95vw] max-h-[90vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                data-testid="pending-preview-modal"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
                    <div className="flex items-center gap-3 min-w-0">
                        {isImage && <Image className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                        {isVideo && <Film className="w-4 h-4 text-purple-500 flex-shrink-0" />}
                        {!isImage && !isVideo && <FileIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                        <span className="text-sm font-medium text-gray-900 truncate">{file.file.name}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                            {file.file.size < 1024 ? `${file.file.size} o` : file.file.size < 1048576 ? `${(file.file.size / 1024).toFixed(1)} Ko` : `${(file.file.size / 1048576).toFixed(1)} Mo`}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                        data-testid="pending-preview-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto flex items-center justify-center p-4 bg-gray-100 min-h-[300px]">
                    {isImage && (
                        <img
                            src={url}
                            alt={file.file.name}
                            className="max-w-full max-h-[75vh] object-contain rounded-md shadow-sm"
                            data-testid="pending-preview-image"
                        />
                    )}
                    {isVideo && (
                        <video
                            src={url}
                            controls
                            autoPlay
                            className="max-w-full max-h-[75vh] rounded-md shadow-sm"
                            data-testid="pending-preview-video"
                        />
                    )}
                    {isPdf && (
                        <iframe
                            src={url}
                            title={file.file.name}
                            className="w-full h-[75vh] rounded-md border bg-white"
                            data-testid="pending-preview-pdf"
                        />
                    )}
                    {!isImage && !isVideo && !isPdf && (
                        <div className="text-center py-12">
                            <FileIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-sm text-gray-500 mb-1">Aperçu non disponible pour ce type de fichier</p>
                            <p className="text-xs text-gray-400">{file.file.type || "Type inconnu"}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export function AttachmentBar({ pendingFiles, setPendingFiles }) {
    const fileInputRef = useRef(null);
    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);
    const [previewFile, setPreviewFile] = useState(null);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        const newPending = files.map((file) => ({
            id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            file,
            preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
            isVideo: file.type.startsWith("video/"),
        }));
        setPendingFiles((prev) => [...prev, ...newPending]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removePendingFile = (pendingId) => {
        setPendingFiles((prev) => {
            const removed = prev.find((f) => f.id === pendingId);
            if (removed?.preview) URL.revokeObjectURL(removed.preview);
            return prev.filter((f) => f.id !== pendingId);
        });
    };

    const handleScreenshot = async () => {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: { mediaSource: "screen" } });
            const track = stream.getVideoTracks()[0];
            const imageCapture = new ImageCapture(track);
            const bitmap = await imageCapture.grabFrame();
            track.stop();
            const canvas = document.createElement("canvas");
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            canvas.getContext("2d").drawImage(bitmap, 0, 0);
            const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
            const fileName = `capture-${new Date().toISOString().replace(/[:.]/g, "-")}.png`;
            const file = new File([blob], fileName, { type: "image/png" });
            setPendingFiles((prev) => [...prev, { id: `pending-${Date.now()}`, file, preview: URL.createObjectURL(blob), isVideo: false }]);
            toast.success("Capture d'écran ajoutée");
        } catch (err) {
            if (err.name !== "AbortError") toast.error("Impossible de capturer l'écran");
        }
    };

    const handleStartRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: { mediaSource: "screen" }, audio: false });
            recordedChunksRef.current = [];
            const recorder = new MediaRecorder(stream, { mimeType: "video/webm" });
            mediaRecorderRef.current = recorder;
            recorder.ondataavailable = (e) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
            recorder.onstop = () => {
                const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
                const fileName = `enregistrement-${new Date().toISOString().replace(/[:.]/g, "-")}.webm`;
                const file = new File([blob], fileName, { type: "video/webm" });
                setPendingFiles((prev) => [...prev, { id: `pending-${Date.now()}`, file, preview: URL.createObjectURL(blob), isVideo: true }]);
                toast.success("Enregistrement ajouté");
                setIsRecording(false);
                stream.getTracks().forEach((t) => t.stop());
            };
            stream.getVideoTracks()[0].onended = () => { if (recorder.state === "recording") recorder.stop(); };
            recorder.start();
            setIsRecording(true);
        } catch (err) {
            if (err.name !== "AbortError") toast.error("Impossible de démarrer l'enregistrement");
        }
    };

    const handleStopRecording = () => {
        if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
    };

    return (
        <div data-testid="attachment-bar">
            {/* Pending files preview — clickable */}
            {pendingFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border mb-3" data-testid="pending-files-preview">
                    {pendingFiles.map((pf) => (
                        <div key={pf.id} className="relative group">
                            <button
                                type="button"
                                onClick={() => setPreviewFile(pf)}
                                className="block focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-md"
                                title="Cliquer pour prévisualiser"
                                data-testid={`preview-pending-${pf.id}`}
                            >
                                {pf.preview ? (
                                    <img src={pf.preview} alt={pf.file.name} className="w-20 h-20 object-cover rounded-md border cursor-pointer hover:opacity-80 transition-opacity" />
                                ) : (
                                    <div className="w-20 h-20 rounded-md border bg-white flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-gray-50 transition-colors">
                                        {pf.isVideo ? <Film className="w-6 h-6 text-purple-500" /> : <FileIcon className="w-6 h-6 text-gray-400" />}
                                        <span className="text-[9px] text-gray-500 text-center px-1 truncate max-w-full">
                                            {pf.file.name.length > 12 ? pf.file.name.slice(0, 10) + "..." : pf.file.name}
                                        </span>
                                    </div>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => removePendingFile(pf.id)}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Toolbar */}
            <div className="flex items-center gap-1.5">
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.log"
                    data-testid="attachment-file-input"
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors border"
                    data-testid="attachment-file-btn"
                >
                    <Paperclip className="w-4 h-4" />
                    Fichier
                </button>
                <button
                    type="button"
                    onClick={handleScreenshot}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors border"
                    data-testid="attachment-screenshot-btn"
                >
                    <Camera className="w-4 h-4" />
                    Capture
                </button>
                {!isRecording ? (
                    <button
                        type="button"
                        onClick={handleStartRecording}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors border"
                        data-testid="attachment-record-btn"
                    >
                        <Video className="w-4 h-4" />
                        Enregistrer
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={handleStopRecording}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors border border-red-200 animate-pulse"
                        data-testid="attachment-stop-btn"
                    >
                        <Square className="w-4 h-4 fill-red-600" />
                        Arrêter
                    </button>
                )}
                {pendingFiles.length > 0 && (
                    <span className="text-xs text-gray-400 ml-2">
                        {pendingFiles.length} fichier{pendingFiles.length > 1 ? "s" : ""} en attente
                    </span>
                )}
            </div>

            {/* Preview modal for pending files */}
            {previewFile && (
                <PendingPreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
            )}
        </div>
    );
}
